import type { Item, Project } from "@/lib/types";
import { ItemRow } from "@/components/ItemRow";
import { SwipeRow } from "@/components/SwipeRow";
import { GroupHeading, groupTone } from "@/components/atoms";
import { listNesting } from "@/lib/hierarchy";

/**
 * A titled group of items, with swipe-to-complete and swipe-to-defer.
 *
 * The desktop Dashboard has its own near-identical Section, but that one takes
 * a `mobile` flag and branches inside. This is the phone's copy: swipe is
 * always on, because on a phone it always should be.
 *
 * Todoist subtasks ride inside their parent's card when both are in this
 * section; given `allItems`, one whose parent is elsewhere names it.
 */
export function MobileSection({
  title, items, projectById, onToggle, onOpen, onDefer, itemsById, subCounts, allItems, emptyText = "Nothing due.",
}: {
  title: string;
  items: Item[];
  projectById: Map<string, Project>;
  onToggle: (item: Item) => void;
  onOpen: (item: Item) => void;
  onDefer?: (item: Item) => void;
  itemsById?: Map<string, Item>;
  subCounts?: Map<string, { done: number; total: number }>;
  allItems?: Item[];
  emptyText?: string;
}) {
  const nest = listNesting(items, allItems);
  return (
    <div style={{ marginBottom: 18 }}>
      <GroupHeading label={title} count={nest.top.length} tone={groupTone(title)} />
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--muted2)", padding: "2px 2px 4px" }}>{emptyText}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {nest.top.map((it) => (
            <SwipeRow
              key={it.id}
              onComplete={() => onToggle(it)}
              onDefer={() => onDefer?.(it)}
              completeLabel={it.status === "done" ? "Reopen" : "Complete"}
            >
              <ItemRow
                item={it}
                projectById={projectById}
                onToggle={onToggle}
                onOpen={onOpen}
                itemsById={itemsById}
                subCounts={subCounts}
                subtasks={nest.childrenOf(it)}
                childrenOf={nest.childrenOf}
                parent={nest.parentOf(it)}
              />
            </SwipeRow>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared padding, with room under the last row for the tab bar and the + button. */
export const mobileScreen: React.CSSProperties = {
  padding: "14px 14px calc(var(--above-tabbar) + 88px)",
};
